import React, { useMemo } from "react";
import Layout from "../Lib/Layout";
import SideBar from "../Lib/SideBar";
import { IconArrowLeft } from "@tabler/icons-react";

const bytesToMB = (bytes = 0) => (bytes / (1024 * 1024)).toFixed(0);

const UsageBar = ({ used = 0, limit, label, subtitle }) => {
  const rawLimit = typeof limit === "string" ? parseFloat(limit) : limit;
  const rawUsed = typeof used === "string" ? parseFloat(used) : used;
  const percent = rawLimit ? Math.min(100, Math.round((rawUsed / rawLimit) * 100)) : 0;
  const statusColor =
    percent < 60 ? "bg-emerald-500" : percent < 90 ? "bg-amber-400" : "bg-rose-500";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-600">{label}</p>
        <p className="text-sm font-medium text-slate-900">
          {limit ? `${used}/${limit}` : "Unlimited"}
        </p>
      </div>
      {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
      {limit && (
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full rounded-full transition-all ${statusColor}`} style={{ width: `${percent}%` }} />
        </div>
      )}
      {!limit && (
        <p className="mt-2 text-xs text-slate-500">
          Your current plan does not cap this resource.
        </p>
      )}
    </div>
  );
};

const BillingPage = (props = {}) => {
  const { planTier = "free", subscription, limitStates = {} } = props;
  const isPaidPlan = planTier !== "free";

  const usageCards = useMemo(() => {
    const snapshots = [
      {
        key: "projects",
        label: "Active Projects",
        format: (x) => x,
      },
      {
        key: "squads",
        label: "Squads Owned",
        format: (x) => x,
      },
      {
        key: "storage",
        label: "Storage (MB)",
        format: bytesToMB,
      },
      {
        key: "invites",
        label: "Invites Sent",
        format: (x) => x,
      },
      {
        key: "applications",
        label: "Applications",
        format: (x) => x,
      },
      {
        key: "ai",
        label: "AI Actions",
        format: (x) => x,
      },
    ];

    return snapshots
      .map(({ key, label, format }) => {
        if (!limitStates[key]) return null;
        const state = limitStates[key];
        const limit =
          typeof state.limit === "number" || state.limit === null
            ? state.limit
            : key === "storage" && state.limit
            ? Number(state.limit)
            : null;
        const used =
          key === "storage" ? Number(state.used || 0) : state.used || 0;
        const formattedUsed = format(used);
        const formattedLimit = limit
          ? format(key === "storage" ? Number(limit) : limit)
          : null;
        return {
          key,
          label,
          used: formattedUsed,
          limit: formattedLimit,
          rawUsed: used,
          rawLimit: limit,
        };
      })
      .filter(Boolean);
  }, [limitStates]);

  const content = (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-6xl px-6 py-12">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => window.location.href = '/dashboard/#/'}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
          >
            <IconArrowLeft size={18} />
            Back to Dashboard
          </button>
        </div>

        <header className="mb-10 flex flex-col gap-2 px-6 py-6">
          <p className="text-sm font-semibold uppercase tracking-wide text-indigo-500">
            Billing & Credits
          </p>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                {isPaidPlan ? "Your Pro Subscription" : "Upgrade to Unlock More"}
              </h1>
              <p className="mt-2 text-base text-slate-600">
                {isPaidPlan
                  ? "Track usage, see renewal info, and manage your plan below."
                  : "You're currently on the Free plan. Upgrade to unlock higher limits, premium support, and priority AI usage."}
              </p>
            </div>
            <div className="flex gap-3">
              {!isPaidPlan ? (
                <>
                  <button 
                    className="rounded-full border border-slate-300 px-5 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-indigo-200 hover:text-indigo-600"
                    onClick={async () => {
                      try {
                        // Generate OAuth code and redirect to Scoper billing
                        const response = await fetch('/api/pms/billing_redirect', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                          }
                        });
                        
                        if (response.ok) {
                          const { redirect_url } = await response.json();
                          // Redirect to Scoper billing page
                          window.location.href = redirect_url;
                        } else {
                          console.error('Failed to generate billing redirect');
                          alert('Failed to redirect to pricing. Please try again.');
                        }
                      } catch (error) {
                        console.error('Error redirecting to pricing:', error);
                        alert('An error occurred. Please try again.');
                      }
                    }}
                  >
                    View Pricing
                  </button>
                  <button 
                    className="rounded-full bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition hover:bg-indigo-500"
                    onClick={async () => {
                      try {
                        // Generate OAuth code and redirect to Scoper billing
                        const response = await fetch('/api/pms/billing_redirect', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                          }
                        });
                        
                        if (response.ok) {
                          const { redirect_url } = await response.json();
                          // Redirect to Scoper billing page
                          window.location.href = redirect_url;
                        } else {
                          console.error('Failed to generate billing redirect');
                          alert('Failed to redirect to billing. Please try again.');
                        }
                      } catch (error) {
                        console.error('Error redirecting to billing:', error);
                        alert('An error occurred. Please try again.');
                      }
                    }}
                  >
                    Upgrade to Pro
                  </button>
                </>
              ) : (
                <button 
                  className="rounded-full bg-white px-5 py-2 text-sm font-semibold text-indigo-600 shadow transition hover:shadow-md"
                  onClick={async () => {
                    try {
                      // Generate OAuth code and redirect to Scoper billing
                      const response = await fetch('/api/pms/billing_redirect', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                        }
                      });
                      
                      if (response.ok) {
                        const { redirect_url } = await response.json();
                        // Redirect to Scoper billing page
                        window.location.href = redirect_url;
                      } else {
                        console.error('Failed to generate billing redirect');
                        alert('Failed to redirect to billing. Please try again.');
                      }
                    } catch (error) {
                      console.error('Error redirecting to billing:', error);
                      alert('An error occurred. Please try again.');
                    }
                  }}
                >
                  Manage Subscription
                </button>
              )}
            </div>
          </div>
        </header>

        <section className="mb-10 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium uppercase tracking-wide text-slate-500">
                  Current Plan
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                  {planTier === "free"
                    ? "Free Starter"
                    : `${planTier.charAt(0).toUpperCase()}${planTier.slice(1)} Plan`}
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  {isPaidPlan
                    ? "Unlimited projects, squads, invites, and storage priority."
                    : "2 active projects, 1 squad, and 200MB total storage."}
                </p>
              </div>
              <div className="rounded-full bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-600">
                {isPaidPlan ? "Pro" : "Free"}
              </div>
            </div>
            {isPaidPlan && (
              <dl className="mt-6 space-y-3 text-sm text-slate-600">
                <div className="flex justify-between">
                  <dt>Status</dt>
                  <dd className="font-medium text-emerald-600 capitalize">
                    {subscription.status || "active"}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt>Renewal Date</dt>
                  <dd className="font-medium text-slate-900">
                    {subscription.expires_at
                      ? new Date(subscription.expires_at).toLocaleDateString()
                      : "—"}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt>Auto Renew</dt>
                  <dd className="font-medium text-slate-900">
                    {subscription.auto_renew === false ? "Off" : "Enabled"}
                  </dd>
                </div>
              </dl>
            )}
          </div>

          <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">
              Why upgrade?
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-600">
              <li className="flex items-start gap-3">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Unlimited active projects & squads to scale faster.
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Increased AI credits for idea validation, suggested tasks, and
                coaching.
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Priority support plus early access to upcoming features.
              </li>
            </ul>
          </div>
        </section>

        <section>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                Usage Snapshot
              </h2>
              <p className="text-sm text-slate-500">
                Monitor the limits tied to your workspace. Upgrade for more headroom.
              </p>
            </div>
          </div>
          {usageCards.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {usageCards.map((card) => (
                <UsageBar
                  key={card.key}
                  label={card.label}
                  used={
                    card.key === "storage" ? `${card.used} MB` : Number(card.used)
                  }
                  limit={
                    card.limit
                      ? card.key === "storage"
                        ? `${card.limit} MB`
                        : Number(card.limit)
                      : null
                  }
                />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
              Usage details are not available yet. Please refresh in a moment.
            </div>
          )}
        </section>
      </div>
    </div>
  );

  return <Layout sidebar={<SideBar />}>{content}</Layout>;
};

export default BillingPage;

